/*
 * @description: 为vue页面提供缓存功能，当跳转到缓存页面的时候，页面保持原样，不做刷新
 * @author: Jason Wong
 * @since: 2019-11-21 17:14:34
 */

const ROUTER_TRACE = [];
const MAX_ROUTER_TRACE_DEEP = 1024;

/**
 * 将router加入到缓存管理中
 * @param {*} router 
 */
function addRouterHook(router) {
    router.afterEach((to, from) => {
        ROUTER_TRACE.push({ name: from.name });
        if (ROUTER_TRACE.length > MAX_ROUTER_TRACE_DEEP) {
            ROUTER_TRACE.shift();
        }
    });
}

export { addRouterHook };

/**
 * 拷贝对象的全部值到另外一个对象
 * @param {Object} dest 
 * @param {Object} src 
 */
function copyOwnKeys(dest,src){
    for(const key in src){
        dest[key] = src[key];
    }
}

/**
 * 跳转上下文对象，记录了跳转页的全部数据，包括$query与$params对象
 */
class JumpContext{
    constructor(dataMap,router){
        if(!dataMap) throw "dataMap can not be null.";
        this._data = dataMap;

        if(!router) return;

        this._data.$query = this._data.query || {};
        copyOwnKeys(this._data.$query,router.query);
        this._data.$params = this._data.params || {};
        copyOwnKeys(this._data.$params,router.params);
    }

    get(key){
        return this._data;
    }

    put(key,value){
        if(key.startsWith('$')){
            throw '"$" is a reserved symbol,use another char as start char please.';
        }
        this._data[key] = value;
    }
}

const HISTORY_MAP = {};
/**
 * 页面包装器，将vue页面包装为一个可缓存对象，而后便可以调用
 * catch方法将页面缓存
 */
class PageWrapper {
    constructor(component) {
        this._component = component;
        this._cacheTargetPages = [];
    }

    when() {
        return this;
    }

    /**
     * 记录要缓存的目标页面
     * @param  {...any} pages 
     */
    goTo(...pages) {
        this._cacheTargetPages.splice(0, 0, ...pages);
        return this;
    }
    /**
     * 缓存当前页面
     */
    cache() {
        if (!this._component || !this._cacheTargetPages || this._cacheTargetPages.length < 1) return;

        const uuid =
            Math.random()
                .toString(36)
                .substr(2) + new Date().getTime().toString(36);
        const self = this;
        const originalBeforeRouteLeave = this._component.beforeRouteLeave;
        this._component.beforeRouteLeave = function(to, from, next) {
            try {
                let pageDescription = null;
                if (to.name) {
                    for (let value of self._cacheTargetPages) {
                        if (value.name && value.name == to.name) {
                            pageDescription = value;
                            break;
                        }
                    }
                }

                if (pageDescription) {
                    let dataInstance = HISTORY_MAP[uuid] = {
                        $data: this.$data
                    };
                    if(pageDescription.onPause){
                        let context = new JumpContext(dataInstance,to);
                        pageDescription.onPause.call(this,context);
                    }
                } else {
                    if (HISTORY_MAP[uuid]) delete HISTORY_MAP[uuid];
                }
            } catch (err) {
                console.error(`Error happends when leave:${err}`);
            } finally {
                if (originalBeforeRouteLeave) originalBeforeRouteLeave(to, from, next);
                else next();
            }
        };

        const originalMounted = this._component.mounted;
        this._component.mounted = function() {
            let pageDescription = null;
            if (ROUTER_TRACE.length > 0) {
                const lastRouterTrace = ROUTER_TRACE[ROUTER_TRACE.length - 1];
                if (lastRouterTrace.name) {
                    for (let value of self._cacheTargetPages) {
                        if (value.name && value.name == lastRouterTrace.name) {
                            pageDescription = value;
                            break;
                        }
                    }
                }
            }

            if (pageDescription && HISTORY_MAP[uuid]) {
                Object.assign(this.$data, HISTORY_MAP[uuid].$data);
                if(pageDescription.onResume){
                    pageDescription.onResume.call(this,HISTORY_MAP[uuid]);
                }
            } else {
                if (originalMounted) originalMounted.call(this);
            }
        };
        return this._component;
    }
}

export function keep(component) {
    return new PageWrapper(component);
}
